<script setup lang="ts">
import { FormLabel, FormInput, FormCheck } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Button from "../../../base-components/Button";
import Preview from "../../../base-components/Preview";
import Litepicker from "../../../base-components/Litepicker";
import { onBeforeMount, reactive, Ref, ref, toRefs } from "vue";
import { useMensajesStore } from "../../../services/mensajes";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { getFirstDayOfMonth, litePickerConfig } from "../../../utils/helper";
import { helpers } from "@vuelidate/validators";
import { getCurrentDate, validarFecha } from "../../../utils/fecha";
import useVuelidate from "@vuelidate/core";
import { usePedidosStore } from "../../../services/pedidos";
import { useSectoresStore } from "../../../services/sectores";
import TomSelect from "../../../custom-components/TomSelect/TomSelect.vue";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

const mensajesStore = useMensajesStore();
const pedidosStore = usePedidosStore();
const sectoresStore = useSectoresStore();
const router = useRouter();

const { filtros } = storeToRefs(pedidosStore);

const emit = defineEmits<{ (e: "cerrar"): void }>();

const filtrosForm = reactive({
  fechaInicio: filtros.value.fechaInicio,
  fechaFin: filtros.value.fechaFin,
  horaInicio: filtros.value.horaInicio,
  horaFin: filtros.value.horaFin,
  cliente: filtros.value.cliente,
  numero: filtros.value.numero,
  origen: filtros.value.origen,
  estado: filtros.value.estado
});

onBeforeMount(async () => {
  if (filtrosForm.fechaInicio === "null" || filtrosForm.fechaInicio === "")
    filtrosForm.fechaInicio = getFirstDayOfMonth();
  if (filtrosForm.fechaFin === "null" || filtrosForm.fechaFin === "")
    filtrosForm.fechaFin = getCurrentDate();
  if (!sectoresStore.sectores.length && !sectoresStore.cargando) {
    await sectoresStore.getFromFirestore();
  }
});

const cargando = ref<boolean>(false);

const esCliente = ref(true);

const rules = {
  fechaInicio: {
    formato: helpers.withMessage(
      `Formato inválido (debe ser: dd/mm/aaaa)`,
      validarFecha
    )
  },
  fechaFin: {
    formato: helpers.withMessage(
      `Formato inválido (debe ser: dd/mm/aaaa)`,
      validarFecha
    )
  }
};

const validate$ = useVuelidate(rules, toRefs(filtrosForm));

const cliente: Ref<string | null> = ref(null);

const handleSeleccionEntidad = (entidad: string | null) => {
  filtrosForm.cliente = entidad;
};

const handlerFiltrar = async () => {
  const result = await validate$.value.$validate();

  if (result) {
    const queryData = {
      fechaInicio: filtrosForm.fechaInicio.replaceAll("-", "/"),
      fechaFin: filtrosForm.fechaFin.replaceAll("-", "/"),
      horaInicio: filtrosForm.horaInicio,
      horaFin: filtrosForm.horaFin,
      receptor: filtrosForm.cliente ? filtrosForm.cliente : "",
      numero: filtrosForm.numero,
      estado: filtrosForm.estado
    };

    Object.assign(filtros.value, { ...filtrosForm });
    router.push({ path: "/pedidos", query: queryData });
    cargando.value = true;

    await pedidosStore.filtrarPedidos().then(res => {
      cargando.value = false;
      mensajesStore.mostrarToast(
        "Filtrado con éxito",
        "...",
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
    });
    cargando.value = false;
  }
};

const configHora = {
  enableTime: true,
  noCalendar: true,
  time_24hr: true,
  minuteIncrement: 1
};
</script>

<template>
  <Preview class="box">
    <form class="validate-form p-2" @submit.prevent="">
      <div
        class="border-b sm:flex-row border-slate-200/60 dark:border-darkmode-400"
      >
        <!-- Fecha comprobante -->
        <!-- Fecha comprobante -->
        <div class="col-span-12 lg:col-span-6 mt-3 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel class="w-40 sw:w-20">Desde</FormLabel>
            </div>

            <div class="w-1/3 flex justify-between">
              <div class="me-2 min-w-28">
                <Litepicker
                  v-model="filtrosForm.fechaInicio"
                  :options="litePickerConfig"
                  :class="{
                    'border-danger': validate$.fechaInicio.$error
                  }"
                />

                <template v-if="validate$.fechaInicio?.$error">
                  <div
                    v-for="(error, index) in validate$.fechaInicio.$errors"
                    :key="index"
                    class="mt-2 text-danger"
                  >
                    {{ error.$message }}
                  </div>
                </template>
              </div>

              <div class="m-2 flex items-center">-</div>

              <div class="ms-2 min-w-28">
                <div class="me-2 min-w-28">
                  <FlatPickr
                    v-model="filtrosForm.horaInicio"
                    :config="configHora"
                    class="w-full h-10 rounded-lg border border-slate-200 text-sm focus:border-[#751B4E1A] focus:ring-0"
                    placeholder="Selecciona hora"
                  />
                </div>

                <template v-if="validate$.fechaFin?.$error">
                  <div
                    v-for="(error, index) in validate$.fechaFin.$errors"
                    :key="index"
                    class="mt-2 text-danger"
                  >
                    {{ error.$message }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="col-span-12 lg:col-span-6 mt-3 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel class="w-40 sw:w-20">Hasta</FormLabel>
            </div>

            <div class="w-1/3 flex justify-between">
              <div class="me-2 min-w-28">
                <Litepicker
                  v-model="filtrosForm.fechaFin"
                  :options="litePickerConfig"
                  :class="{
                    'border-danger': validate$.fechaFin.$error
                  }"
                />
              </div>
              <div class="m-2 flex items-center">-</div>

              <div class="ms-2 min-w-28">
                <div class="me-2 min-w-28">
                  <FlatPickr
                    v-model="filtrosForm.horaFin"
                    :config="configHora"
                    class="w-full h-10 rounded-lg border border-slate-200 text-sm focus:border-[#751B4E1A] focus:ring-0"
                    placeholder="Selecciona hora"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Nro pedido -->
        <div class="col-span-12 lg:col-span-6 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Número
              </FormLabel>
            </div>
            <div class="me-2 w-1/3 min-w-[262px]">
              <FormInput
                type="text"
                v-model.trim="filtrosForm.numero"
                placeholder="Número"
              />
            </div>
          </div>
        </div>
        <div class="col-span-12 lg:col-span-6 mt-3 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Sector
              </FormLabel>
            </div>
            <div class="w-1/3 min-w-[262px]">
              <div class="flex-1">
                <TomSelect
                  v-model.trim="filtrosForm.origen"
                  style="width: 100%"
                  :options="{
                    placeholder: 'Sector'
                  }"
                >
                  <option value="">Todos</option>
                  <option
                    v-for="sector in sectoresStore.sectores"
                    :key="sector.id"
                    :value="sector.id"
                  >
                    {{ sector.nombre }}
                  </option>
                </TomSelect>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-12 lg:col-span-6 mt-3 mb-5">
          <div class="flex">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Estado
              </FormLabel>
            </div>
            <div class="w-1/3 min-w-[262px]">
              <div class="flex-1">
                <TomSelect
                  v-model.trim="filtrosForm.estado"
                  style="width: 100%"
                  :options="{
                    placeholder: 'Estado'
                  }"
                >
                  <option value="">Todas</option>
                  <option value="borrador">Borrador</option>
                  <option value="abierto">Abierto</option>
                  <option value="cerrado">Cerrado</option>
                  <option value="cancelado">Cancelado</option>
                </TomSelect>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!cargando" class="flex mt-6">
        <div class="flex-1 text-left mt-3">
          <Button
            variant="secondary"
            class="mb-2 mr-1"
            @click="
              () => {
                emit('cerrar');
              }
            "
          >
            Cancelar</Button
          >
        </div>
        <div class="flex-1 text-right">
          <Button
            variant="primary"
            class="mb-2 mr-1 mt-3"
            @click="handlerFiltrar"
          >
            Filtrar
          </Button>
        </div>
      </div>
      <div v-else class="flex my-6">
        <div class="mx-auto">
          <LoadingIcon icon="oval" class="w-8 h-8" />
        </div>
      </div>
    </form>
  </Preview>
</template>

<style scoped>
:deep(.ts-dropdown) {
  max-height: 12rem;
  overflow-y: auto;
}
</style>
